import React, { useState, useCallback, useRef, useEffect } from 'react';
import Typography from 'new/elements/Typography';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import { getDateString } from 'js/lib/DateUtils';
import {
  FileWithPreview,
  FileUploadConfigurationProps,
} from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import { FileUploadAdvancedConfigurationSection } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadAdvancedConfigurationSection';
import { FilePreviewModal } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FilePreviewModal';
import { FileList } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileList';
import { useFileManager, FileValidationError } from './hooks/useFileManager';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { generateFileId } from './utils/fileUtils';
import { fileUploadConfig } from './config/fileUploadConfig';

import styles from './FileUploadConfiguration.module.scss';
import { TextField, Alert, Snackbar, Box } from '@mui/material';
import { useGetPaging } from 'js/hooks/useGetPaging';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

function getUploadableFiles(files: FileWithPreview[]): FileWithPreview[] {
  return files.filter((f) => !f.isBlocked && !f.isOverLimit);
}

export function FileUploadConfiguration(props: FileUploadConfigurationProps) {
  const {
    sourceBeingEdited,
    selectedFile,
    selectedFiles: propSelectedFiles,
    enableCategoryHeader = true,
    allowMultiple = true,
    showPreview = true,
    buildSourceInfo,
    getCustomProcessorId,
  } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<FileWithPreview | null>(null);
  const [validationErrors, setValidationErrors] = useState<FileValidationError[]>([]);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  // Use file manager hook
  const {
    files: managedFiles,
    allFilesForDisplay,
    addFiles,
    removeFile,
    updateFileProgress,
    updateFileStatus,
    maxFiles,
    remainingSlots,
  } = useFileManager({
    allowMultiple,
    autoPreview: showPreview,
    trackExcludedFiles: true,
  });

  // Sync managed files with props - show all files for display (including excluded)
  const selectedFiles = propSelectedFiles || allFilesForDisplay;

  // Handle files selected callback
  const handleFilesSelected = useCallback(
    async (files: FileList | File[]) => {
      const result = await addFiles(files);

      // Show validation errors if any
      if (result.errors.length > 0) {
        setValidationErrors(result.errors);
        setShowErrorSnackbar(true);
      }

      // IMPORTANT: Only notify parent with uploadable files (not blocked/over-limit files)
      // This fixes the bug where blocked files were still being uploaded
      const uploadableFiles = result.addedFiles;

      if (allowMultiple && props.onFilesSelected) {
        props.onFilesSelected(uploadableFiles.map((f) => f as unknown as File));
      }
      // Call onFileSelected only for single-file flows (or single selection)
      if (
        props.onFileSelected &&
        uploadableFiles.length > 0 &&
        (!allowMultiple || uploadableFiles.length === 1)
      ) {
        props.onFileSelected(uploadableFiles[0] as unknown as File);
      }

      // Auto-generate source name if not set (use first uploadable file)
      if (!props.name && uploadableFiles.length > 0) {
        const firstFile = uploadableFiles[0];
        const dotIndex = firstFile.name.lastIndexOf('.');
        const sourceName =
          (dotIndex > -1 ? firstFile.name.substr(0, dotIndex) : firstFile.name) +
          ' - ' +
          getDateString(new Date(), 'MMDD');
        props.onMetaInfoChanged('name', sourceName);
      }

      // Reset input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles, allowMultiple, props],
  );

  // Close error snackbar
  const handleCloseErrorSnackbar = useCallback(() => {
    setShowErrorSnackbar(false);
    setValidationErrors([]);
  }, []);

  // Drag and drop hook
  const { isDragging, dragHandlers, dropZoneRef } = useDragAndDrop({
    onDrop: (files) => {
      handleFilesSelected(files);
    },
    disabled: false,
  });

  // Sync with prop changes
  useEffect(() => {
    if (propSelectedFiles) {
      // Files are controlled by parent
      return;
    }
    if (selectedFile && managedFiles.length === 0) {
      const fileWithPreview = Object.assign(selectedFile, {
        id: generateFileId(selectedFile),
        status: 'pending' as const,
        uploadProgress: 0,
        backendUploadProgress: 0,
      }) as FileWithPreview;
      addFiles([fileWithPreview]);
    }
  }, [propSelectedFiles, selectedFile, managedFiles.length, addFiles]);

  const hasSelectedFile = selectedFiles.length > 0 || sourceBeingEdited;

  const onFileChange: React.ComponentProps<'input'>['onChange'] = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFilesSelected(files);
    }
  };

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      removeFile(fileId);

      if (props.onFileRemoved) {
        props.onFileRemoved(fileId);
      }

      // Update parent component with only uploadable files (exclude blocked/over-limit)
      const updatedFiles = selectedFiles
        .filter((f) => f.id !== fileId)
        .filter((f) => !f.isBlocked && !f.isOverLimit);

      if (updatedFiles.length === 0) {
        if (props.onFileSelected) {
          props.onFileSelected(null);
        }
        if (props.onFilesSelected && allowMultiple) {
          props.onFilesSelected([]);
        }
      } else {
        if (allowMultiple && props.onFilesSelected) {
          props.onFilesSelected(updatedFiles.map((f) => f as unknown as File));
        }
        if (
          props.onFileSelected &&
          updatedFiles.length > 0 &&
          (!allowMultiple || updatedFiles.length === 1)
        ) {
          props.onFileSelected(updatedFiles[0] as unknown as File);
        }
      }
    },
    [removeFile, selectedFiles, props, allowMultiple],
  );

  const handlePreviewClick = useCallback((file: FileWithPreview) => {
    setPreviewFile(file);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  const { sourceConfigById } = useGetPaging();

  const selectedConnection = props.selectedConnection;

  const inputValue = props?.name || '';

  return (
    <div className={styles.container}>
      <FormConfigurationStep
        enableCategoryHeader={enableCategoryHeader}
        connectionName="File Upload"
        connectionIcon={
          <img
            width={15}
            height={15}
            src="https://cdn.nexla.io/ui/assets/vendors/logos/logo-file_upload_v2.svg"
            alt="file-upload-icon"
          />
        }
        headerChildren={
          selectedFiles.length > 0 ? (
            <TextField
              size="x-small"
              placeholder="Data Source Name*"
              value={inputValue}
              onChange={(event: any) => props?.onMetaInfoChanged('name', event.target.value)}
            />
          ) : null
        }
      >
        <div className={styles.content}>
          <section className={styles.description}>
            <Typography variant="bodyDefault">
              Effortlessly create flows from files. Upload one or more files, and Nexla will
              automatically generate a Nexset. From there, easily modify and route the data to any
              Destination.
            </Typography>
          </section>

          <section className={styles.form}>
            <div className={styles.padding}>
              {/* File Upload Zone */}
              <div
                ref={dropZoneRef}
                className={`${styles.inputContainer} ${isDragging ? styles.dragging : ''}`}
                {...dragHandlers}
              >
                <label htmlFor="select_file_input" className={styles.dropZoneLabel}>
                  <div className={styles.dropZoneContent}>
                    <div className={styles.uploadIconWrapper}>
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.uploadIcon}
                      >
                        <path
                          d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M17 8L12 3L7 8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M12 3V15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <Typography variant="bodyLarge" className={styles.dropZoneText}>
                      {isDragging ? 'Drop files here' : 'Upload Files'}
                    </Typography>
                    <Typography variant="bodySmall" className={styles.dropZoneHint}>
                      {isDragging
                        ? 'Release to upload'
                        : allowMultiple
                          ? 'Drag and drop your files here, or click to browse'
                          : 'Drag and drop your file here, or click to browse'}
                    </Typography>
                    {allowMultiple && (
                      <Typography variant="caption" className={styles.dropZoneInfo}>
                        {`Max ${maxFiles} files${remainingSlots < maxFiles ? ` (${remainingSlots} remaining)` : ''}`}
                        {fileUploadConfig.blockedExtensions.length > 0 && (
                          <> • Not allowed: {fileUploadConfig.blockedExtensionsDisplay}</>
                        )}
                      </Typography>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="select_file_input"
                    className={styles.inputFileUpload}
                    onChange={onFileChange}
                    multiple={allowMultiple}
                    // Note: The accept attribute helps suggest file types to the browser,
                    // but doesn't prevent all file types. Validation still happens after selection.
                    accept={fileUploadConfig.blockedExtensions.length > 0 ? undefined : '*/*'}
                  />
                </label>
              </div>

              {/* File List */}
              {selectedFiles.length > 0 && (
                <FileList
                  files={selectedFiles}
                  onRemove={handleRemoveFile}
                  onPreview={showPreview ? handlePreviewClick : undefined}
                  allowMultiple={allowMultiple}
                />
              )}

              {/* Configuration Options */}
              {hasSelectedFile && (
                <div className={styles.options}>
                  <FileUploadAdvancedConfigurationSection
                    {...props}
                    buildSourceInfo={buildSourceInfo}
                    getCustomProcessorId={getCustomProcessorId}
                    uploadedFiles={getUploadableFiles(selectedFiles)}
                  />
                </div>
              )}
            </div>
          </section>
        </div>
      </FormConfigurationStep>

      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal file={previewFile} open={!!previewFile} onClose={handleClosePreview} />
      )}

      {/* Validation Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={8000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="warning"
          icon={<WarningAmberIcon fontSize="medium" />}
          sx={{
            width: '100%',
            maxWidth: '500px',
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
            '& .MuiAlert-message': {
              padding: '8px 0',
            },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid var(--color-warning-300, #fcd34d)',
            backgroundColor: 'var(--color-warning-50, #fffbeb)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {validationErrors.map((error, index) => (
              <Box key={index} sx={{ lineHeight: 1.5 }}>
                {error.fileName ? (
                  <>
                    <strong>{error.fileName}</strong>: {error.message}
                  </>
                ) : (
                  error.message
                )}
              </Box>
            ))}
          </Box>
        </Alert>
      </Snackbar>
    </div>
  );
}
